
import { useState, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import { useNavigate, useLocation, matchPath } from 'react-router-dom';
import {
    Plus, PlusCircle, Play, Edit, Trash2, Calendar, Clock,
    MoreHorizontal, Search, FileJson, Activity,
    ArrowRight, Sparkles, Filter, List, LayoutGrid, Copy, Download, Upload,
    CheckCircle2, GitBranch, History, FolderOpen, UploadCloud, FileText, HelpCircle, AlertTriangle
} from 'lucide-react';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from '@/components/ui/button';
import { ConfirmModal } from '@/components/common/ConfirmModal';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
// @ts-ignore
import { StandardPageLayout, Button as CommonButton } from '../components/Common.jsx';
import { useNotification } from '@/context/NotificationContext';
import FlowEditorPage from './FlowEditorPage';
import { useAuth } from '@/context/useAuth';
import { hasPermission } from '@/utils/permissions';
import { useTimezoneFormatter } from '@/hooks/useTimezoneFormatter';
import { useTenantExportBranding } from '@/hooks/useTenantExportBranding';
import { apiGet, apiDelete, apiPost } from '../api';
import FlowVersionsPanel from '../components/flows/FlowVersionsPanel';
import PublishAppModal from '../components/flows/PublishAppModal';
import { STUDIOS, STUDIO_LABELS, STUDIO_DESCRIPTIONS } from '../constants/studios.js';
import { PERMISSIONS } from '@/constants/permissions';
// @ts-ignore
import { useFlowTour } from '../tours/useFlowTour';
// @ts-ignore
import { TourEngine } from '../tours/TourEngine.jsx';
import packageJson from '../../package.json';

const BUILD_VERSION = packageJson.version;



interface EtlFlow {
    id: string;
    name: string;
    description: string;
    updatedAt: string;
    scheduleCron?: string;
    isActive: boolean;
    lastRunStatus?: 'Success' | 'Failed' | 'Running';
    lastRunTime?: string;
    flowType?: string;
    // Lifecycle fields
    status?: 'Draft' | 'Published';
    version_number?: number;
    published_at?: string | null;
    draft_version_label?: string;
    latest_published_version?: number;
    active_content_version?: string;
    // Ownership / sharing
    is_owner?: boolean;   // false = shared with me (view-only)
    shared_with?: string[] | null;
}

type StatusFilter = 'all' | 'Draft' | 'Published';

export default function FlowsPage({ studioMode = STUDIOS.ALL, refreshContent }: { studioMode?: string, refreshContent?: () => void }) {
    const { token, user } = useAuth();
    const [view, setView] = useState<'list' | 'editor'>('list');
    const [selectedFlowId, setSelectedFlowId] = useState<string | null>(null);
    const [loadSnapshotData, setLoadSnapshotData] = useState<any>(null);
    const location = useLocation();

    useEffect(() => {
        const match = matchPath({ path: '/flows/:flowId/*', end: false }, location.pathname);
        if (match && match.params.flowId) {
            setSelectedFlowId(match.params.flowId);
            setView('editor');
            window.history.replaceState(null, '', '/flows');
        } else if (location.pathname === '/flows') {
            setView('list');
            setSelectedFlowId(null);
        }
    }, [location.pathname]);

    useEffect(() => {
        const handleReset = (e: any) => {
            const pageId = e.detail?.pageId;
            // Handle specific studio modes and general flows page
            if (pageId === 'flows' || pageId === 'data_studio' || pageId === 'ml_pipelines' || pageId === 'workflow_studio') {
                setView('list');
                setSelectedFlowId(null);
                setLoadSnapshotData(null);
                window.history.replaceState(null, '', '/flows');
            }
        };
        window.addEventListener('reset-page-state', handleReset);
        return () => window.removeEventListener('reset-page-state', handleReset);
    }, []);

    // ── Flow Builder Tour (Persists across List/Editor views) ──────────────
    const { steps: tourSteps, run: tourRun, setRun: setTourRun, markSeen: markTourSeen, restartTour } = useFlowTour(user?.id, token);

    let content;
    if (view === 'editor') {
        content = (
            <div className="h-full w-full">
                <FlowEditorPage
                    flowId={selectedFlowId}
                    studioMode={studioMode}
                    refreshContent={refreshContent}
                    initialSnapshot={loadSnapshotData}
                    onBack={() => {
                        setView('list');
                        setSelectedFlowId(null);
                        setLoadSnapshotData(null);
                    }}
                />
            </div>
        );
    } else {
        content = (
            <FlowListContent
                studioMode={studioMode}
                onEdit={(id, snapshot?) => {
                    setSelectedFlowId(id);
                    setLoadSnapshotData(snapshot || null);
                    setView('editor');
                }}
                onCreate={() => {
                    setSelectedFlowId(null);
                    setView('editor');
                }}
                refreshContent={refreshContent}
                onRestartTour={restartTour}
            />
        );
    }

    return (
        <>
            {content}
            <TourEngine
                steps={tourSteps}
                run={tourRun}
                setRun={setTourRun}
                markSeen={markTourSeen}
            />
        </>
    );
}

function FlowListContent({ onEdit, onCreate, studioMode, refreshContent, onRestartTour }: {
    onEdit: (id: string, snapshot?: any) => void;
    onCreate: () => void;
    studioMode: string;
    refreshContent?: () => void;
    onRestartTour?: () => void;
}) {
    const navigate = useNavigate();
    const [flows, setFlows] = useState<EtlFlow[]>([]);
    const [view, _setView] = useState<'grid' | 'list'>(() =>
        (localStorage.getItem('flows-view-mode') as 'grid' | 'list') || 'grid'
    );
    const setView = (mode: 'grid' | 'list') => {
        localStorage.setItem('flows-view-mode', mode);
        _setView(mode);
    };
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<StatusFilter>(() =>
        (localStorage.getItem('flows-status-filter') as StatusFilter) || 'all'
    );
    const [sortOption, setSortOption] = useState<'updated_desc' | 'updated_asc' | 'name_asc' | 'name_desc'>('updated_desc');

    // Panel states
    const [versionsPanelFlow, setVersionsPanelFlow] = useState<EtlFlow | null>(null);
    const [loadSnapshotFlow, setLoadSnapshotFlow] = useState<EtlFlow | null>(null);

    // Publish dialog
    const [publishConfig, setPublishConfig] = useState<EtlFlow | null>(null);
    const [nextVersionLabel, setNextVersionLabel] = useState<string>('');

    const { token, user } = useAuth();
    const { formatDate: fmtDate } = useTimezoneFormatter();
    const { addNotification } = useNotification();
    const tenantBranding = useTenantExportBranding();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [deleteConfirm, setDeleteConfirm] = useState<EtlFlow | null>(null);
    const [deleteInput, setDeleteInput] = useState('');

    // Description editor modal
    const [descModal, setDescModal] = useState<{ id: string; name: string; desc: string } | null>(null);
    const [editingDesc, setEditingDesc] = useState('');
    const [savingDesc, setSavingDesc] = useState(false);

    const saveDescription = async () => {
        if (!descModal) return;
        setSavingDesc(true);
        try {
            const url = `${import.meta.env.VITE_API_BASE_URL || ''}/api/flows/${descModal.id}`;
            await fetch(url, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ description: editingDesc.trim() }),
            });
            setFlows(prev => prev.map(f => f.id === descModal.id ? { ...f, description: editingDesc.trim() } : f));
            addNotification('Description saved', 'success');
            setDescModal(null);
        } catch {
            addNotification('Failed to save description', 'error');
        } finally {
            setSavingDesc(false);
        }
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        addNotification("Copied to clipboard", "success");
    };

    useEffect(() => { if (token) fetchFlows(); }, [token]);

    const fetchFlows = async () => {
        try {
            const data = await apiGet('/flows', token);
            if (data) setFlows(data);
        } catch (e) {
            addNotification("Failed to load flows", "error");
        } finally {
            setIsLoading(false);
        }
    };

    const setStatusFilterPersist = (f: StatusFilter) => {
        localStorage.setItem('flows-status-filter', f);
        setStatusFilter(f);
    };

    const confirmDelete = (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        const flow = flows.find(f => f.id === id);
        if (flow) {
            setDeleteConfirm(flow);
            setDeleteInput('');
        }
    };

    const handleDelete = async () => {
        if (!deleteConfirm) return;
        try {
            await apiDelete(`/flows/${deleteConfirm.id}`, token);
            addNotification("Flow deleted successfully", "success");
            setFlows(prev => prev.filter(f => f.id !== deleteConfirm.id));
            window.dispatchEvent(new Event('refresh-sidebar'));
        } catch (e) {
            addNotification("Failed to delete flow", "error");
        } finally {
            setDeleteConfirm(null);
            setDeleteInput('');
        }
    };

    const handleRun = async (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        onEdit(id);
    };

    // Open publish modal with accurate next-version label fetched live from the API
    const openPublishModal = async (flow: EtlFlow) => {
        setPublishConfig(flow);
        try {
            const versions = await apiGet(`/flows/${flow.id}/versions`, token);
            const maxVer = Array.isArray(versions) && versions.length > 0
                ? Math.max(...versions.map((v: any) => v.version_number || 0))
                : 0;
            setNextVersionLabel(`v${maxVer + 1}`);
        } catch {
            // Fallback to local version_number if API fails
            setNextVersionLabel(`v${(flow.version_number || 0) + 1}`);
        }
    };

    const handlePublish = async (
        name: string,
        description: string,
        contentTypeId: number,
        sharedWith: string[],
        folderName: string,
        status?: string,
        overwrite = false,
        linkedDashboardId?: string,
        versionLabel?: string
    ) => {
        if (!publishConfig) return;

        try {
            await apiPost(`/flows/${publishConfig.id}/publish`, token, {
                name, description, content_type_id: contentTypeId, sharedWith, folderName, status,
                linked_dashboard_id: linkedDashboardId,
                version_label: versionLabel || undefined,
            });
            addNotification("Flow published successfully", "success");
            await fetchFlows();
            window.dispatchEvent(new Event('refresh-sidebar'));
            setPublishConfig(null);
        } catch (e) {
            console.error("Publish failed", e);
            throw e;
        }
    };

    const handleUnpublish = async (flow: EtlFlow) => {
        try {
            await apiPost(`/flows/${flow.id}/unpublish`, token, {});
            addNotification(`"${flow.name}" unpublished — back to Draft`, "success");
            await fetchFlows();
            window.dispatchEvent(new Event('refresh-sidebar'));
        } catch (e: any) {
            addNotification(e?.message || "Unpublish failed", "error");
        }
    };

    const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            const text = await file.text();
            const parsedData = JSON.parse(text);
            const newName = `${parsedData.name || parsedData.Name || 'Imported Flow'} (Imported)`;
            let importType = parsedData.type || parsedData.FlowType || 'workflow';
            if (studioMode === STUDIOS.DATA) importType = 'data_pipeline';
            if (studioMode === STUDIOS.ML) importType = 'ml_flow';
            if (studioMode === STUDIOS.WORKFLOW) importType = 'workflow';
            // Strip environment-specific connection IDs so they don't carry over from
            // the source environment (avoids "Could not find connection: <uuid>" errors).
            // Users will be prompted to re-select their connections after import.
            const CONNECTION_FIELDS = [
                'connectionId', 'connectionName', 'connection_id',
                'dataSourceId', 'data_source_id',
            ];
            const sanitizedNodes = (parsedData.nodes || []).map((node: any) => {
                const data = { ...(node.data || node.config || {}) };
                CONNECTION_FIELDS.forEach(f => { if (f in data) delete data[f]; });
                return { ...node, data, config: data };
            });
            const graphData = { nodes: sanitizedNodes, edges: parsedData.edges || [], variables: parsedData.variables || [] };
            const payload = { Name: newName, Description: parsedData.description || "", FlowType: importType, GraphJson: JSON.stringify(graphData) };
            await apiPost('/flows', token, payload);
            addNotification("Flow imported successfully", "success");
            fetchFlows();
        } catch (err: any) {
            addNotification(`Import failed: ${err.message || 'Invalid format'}`, "error");
        } finally {
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleExport = async (flowId: string, flowName: string, e: React.MouseEvent) => {
        e.stopPropagation();
        try {
            const flowData = await apiGet(`/flows/${flowId}`, token);
            // graphJson is a serialized string — parse it to get nodes/edges/variables
            let nodes = [], edges = [], variables = [];
            try {
                const graph = JSON.parse(flowData.graphJson || '{}');
                nodes = graph.nodes || [];
                edges = graph.edges || [];
                variables = graph.variables || [];
            } catch { /* keep empty arrays */ }
            const exportData = {
                id: flowId,
                name: flowData.name,
                description: flowData.description || "",
                type: flowData.flowType,
                nodes,
                edges,
                variables,
                build_version: BUILD_VERSION,
                _tenant_branding: {
                    exported_at: dayjs().toISOString(),
                    watermark: tenantBranding.watermark?.text || null
                }
            };
            const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(exportData, null, 2));
            const el = document.createElement('a');
            el.setAttribute("href", dataStr);
            el.setAttribute("download", `${flowData.name || 'flow'}_export.json`);
            document.body.appendChild(el);
            el.click();
            el.remove();
            addNotification("Flow exported successfully", "success");
        } catch (err) {
            addNotification("Failed to export flow", "error");
        }
    };

    // Handle snapshot loaded from versions panel → open editor with that snapshot
    const handleLoadSnapshot = async (version: any) => {
        if (!loadSnapshotFlow) return;
        try {
            const detail = await apiGet(`/flows/${loadSnapshotFlow.id}/versions/${version.id}`, token);
            onEdit(loadSnapshotFlow.id, detail?.flow_snapshot || null);
            addNotification(`Loaded snapshot: ${version.version}`, "info");
        } catch {
            addNotification("Failed to load snapshot", "error");
        }
        setLoadSnapshotFlow(null);
    };

    const filteredFlows = flows.filter(f => {
        let targetType = 'workflow';
        if (studioMode === STUDIOS.DATA) targetType = 'data_pipeline';
        if (studioMode === STUDIOS.ML) targetType = 'ml_flow';
        if (studioMode === STUDIOS.WORKFLOW) targetType = 'workflow';
        if (studioMode !== STUDIOS.ALL && f.flowType !== targetType) return false;
        if (statusFilter !== 'all' && f.status !== statusFilter) return false;
        return (
            f.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (f.description && f.description.toLowerCase().includes(searchQuery.toLowerCase()))
        );
    });
    const sortedFlows = [...filteredFlows].sort((a, b) => {
        const dateA = new Date(a.updatedAt || 0).getTime();
        const dateB = new Date(b.updatedAt || 0).getTime();
        if (sortOption === 'updated_desc') return dateB - dateA;
        if (sortOption === 'updated_asc') return dateA - dateB;
        if (sortOption === 'name_asc') return (a.name || '').localeCompare(b.name || '');
        if (sortOption === 'name_desc') return (b.name || '').localeCompare(a.name || '');
        return 0;
    });

    const headerActions = (
        <div className="flex gap-2 z-10 items-center">
            <input type="file" ref={fileInputRef} onChange={handleImport} accept=".json" className="hidden" />
            {onRestartTour && (
                <Button variant="outline" size="icon" onClick={onRestartTour} title="Take a Quick Tour" className="h-[30px] w-[30px] text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-200">
                    <HelpCircle className="h-4 w-4" />
                </Button>
            )}
            {hasPermission(user, PERMISSIONS.FLOWS_CREATE) && (
                <Button id="flow-list-add-btn" onClick={onCreate} className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-md shadow-blue-500/20 transition-all duration-200 active:scale-95 font-semibold">
                    <PlusCircle className="mr-2 h-4 w-4" /> CREATE {studioMode === STUDIOS.ALL ? 'FLOW' : (studioMode === STUDIOS.DATA ? 'PIPELINE' : 'WORKFLOW')}
                </Button>
            )}
            {hasPermission(user, PERMISSIONS.FLOWS_IMPORT) && (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-[30px] w-[30px] dark:text-slate-200 dark:hover:bg-slate-800">
                            <MoreHorizontal className="h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => fileInputRef.current?.click()} className="cursor-pointer">
                            <Upload className="mr-2 h-4 w-4 text-blue-500" />
                            <span>Import Flow JSON</span>
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            )}
        </div>
    );

    const filterControls = (
        <div className="flex items-center gap-3 flex-wrap">
            {/* Search */}
            <div className="relative max-w-md group w-56">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 group-focus-within:text-[#002F5D] dark:text-slate-500 transition-colors" />
                <input
                    type="text"
                    placeholder="Search flows..."
                    className="w-full bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 py-2 pl-10 pr-4 rounded-xl shadow-sm text-sm focus:ring-2 focus:ring-blue-100 dark:text-slate-100 transition-shadow outline-none placeholder:text-gray-400 dark:placeholder:text-slate-500"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
            </div>

            {/* Filter icon + checkbox dropdown */}
            <FilterDropdown value={statusFilter} onChange={setStatusFilterPersist} />
            <select
                className="h-9 rounded-lg border border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 text-xs font-medium text-gray-600 dark:text-slate-300"
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value as any)}
            >
                <option value="updated_desc">Recently Updated</option>
                <option value="updated_asc">Oldest Updated</option>
                <option value="name_asc">Name A-Z</option>
                <option value="name_desc">Name Z-A</option>
            </select>

            {/* Grid/List toggle */}
            <div className="flex items-center bg-white dark:bg-slate-900 rounded-lg border border-gray-200 dark:border-slate-800 p-1">
                <Button variant={view === 'grid' ? 'secondary' : 'ghost'} size="sm" className={`px-2 h-7 ${view === 'grid' ? 'dark:bg-slate-800 dark:text-white' : 'dark:text-slate-400'}`} onClick={() => setView('grid')}>
                    <LayoutGrid className="w-4 h-4" />
                </Button>
                <Button variant={view === 'list' ? 'secondary' : 'ghost'} size="sm" className={`px-2 h-7 ${view === 'list' ? 'dark:bg-slate-800 dark:text-white' : 'dark:text-slate-400'}`} onClick={() => setView('list')}>
                    <List className="w-4 h-4" />
                </Button>
            </div>
        </div>
    );

    return (
        <StandardPageLayout
            title={STUDIO_LABELS[studioMode]}
            description={STUDIO_DESCRIPTIONS[studioMode]}
            headerActions={headerActions}
            filterControls={filterControls}
        >
            {isLoading ? (
                <div className="flex-1 flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
                </div>
            ) : sortedFlows.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center text-center p-12 border-2 border-dashed border-gray-200 dark:border-slate-700 rounded-3xl bg-gray-50/50 dark:bg-slate-900/50 mt-4">
                    <div className="bg-white dark:bg-slate-800 p-4 rounded-full shadow-sm mb-4">
                        <FileJson className="h-8 w-8 text-gray-300 dark:text-slate-500" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">No flows found</h3>
                    <p className="text-gray-500 dark:text-slate-400 text-sm mt-2 max-w-xs">
                        {statusFilter !== 'all' ? `No ${statusFilter} flows found. Try switching filter to "All".` : 'Create your first flow to get started.'}
                    </p>
                    <Button variant="ghost" onClick={onCreate} className="mt-6 text-primary hover:bg-gray-100 dark:hover:bg-slate-800">
                        Create Flow <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                </div>
            ) : view === 'grid' ? (
                <div id="flow-list-container" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 content-start pb-10">
                    {sortedFlows.map(flow => (
                        <FlowCard
                            key={flow.id}
                            flow={flow}
                            onEdit={(id) => onEdit(id)}
                            onPublish={(f) => openPublishModal(f)}
                            onUnpublish={handleUnpublish}
                            onVersions={(f, e) => { e.stopPropagation(); setVersionsPanelFlow(f); }}
                            onLoadSnapshot={(f, e) => { e.stopPropagation(); setLoadSnapshotFlow(f); }}
                            onDelete={confirmDelete}
                            onExport={handleExport}
                            canExport={hasPermission(user, 'flows:export')}
                            onCopy={copyToClipboard}
                            onDescription={(f, e) => { e.stopPropagation(); setEditingDesc(f.description || ''); setDescModal({ id: f.id, name: f.name, desc: f.description || '' }); }}
                            fmtDate={fmtDate}
                        />
                    ))}
                </div>
            ) : (
                <FlowListView
                    flows={sortedFlows}
                    onEdit={(id) => onEdit(id)}
                    onPublish={(f) => openPublishModal(f)}
                    onUnpublish={handleUnpublish}
                    onVersions={(f) => setVersionsPanelFlow(f)}
                    onLoadSnapshot={(f) => setLoadSnapshotFlow(f)}
                    onDelete={confirmDelete}
                    onExport={handleExport}
                    canExport={hasPermission(user, 'flows:export')}
                    onCopy={copyToClipboard}
                    onDescription={(f, e) => { e.stopPropagation(); setEditingDesc(f.description || ''); setDescModal({ id: f.id, name: f.name, desc: f.description || '' }); }}
                    fmtDate={fmtDate}
                />
            )}

            <Dialog open={!!deleteConfirm} onOpenChange={(isOpen) => {
                if (!isOpen) {
                    setDeleteConfirm(null);
                    setDeleteInput('');
                }
            }}>
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle>Delete Flow</DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        {deleteConfirm?.status === 'Published' ? (
                            <div className="space-y-4">
                                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 flex items-start gap-3">
                                    <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                                    <div>
                                        <h4 className="text-sm font-semibold text-amber-800 dark:text-amber-400">Flow is Published</h4>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <p className="text-sm text-gray-500 dark:text-slate-400 mb-4">
                                Are you sure you want to delete the flow <strong>{deleteConfirm?.name}</strong>? This action cannot be undone.
                            </p>
                        )}
                        <div className="mt-4">
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                Please type <strong className="select-all">{deleteConfirm?.name}</strong> to confirm.
                            </label>
                            <Input
                                type="text"
                                value={deleteInput}
                                onChange={(e) => setDeleteInput(e.target.value)}
                                placeholder="Flow name"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setDeleteConfirm(null); setDeleteInput(''); }}>Cancel</Button>
                        {deleteConfirm?.status === 'Published' ? (
                            <Button
                                variant="destructive"
                                disabled={deleteInput !== deleteConfirm?.name}
                                onClick={async () => {
                                    try {
                                        await apiPost(`/flows/${deleteConfirm.id}/unpublish`, token, {});
                                        await handleDelete();
                                    } catch (e: any) {
                                        addNotification(e.message || "Failed to unpublish and delete flow", "error");
                                    }
                                }}
                            >
                                <UploadCloud className="w-4 h-4 mr-2" /> Unpublish & Delete
                            </Button>
                        ) : (
                            <Button variant="destructive" disabled={deleteInput !== deleteConfirm?.name} onClick={handleDelete}>
                                <Trash2 className="w-4 h-4 mr-2" /> Delete
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <PublishAppModal
                open={!!publishConfig}
                onOpenChange={(open) => !open && setPublishConfig(null)}
                defaultName={publishConfig?.name || ''}
                initialVersionLabel={nextVersionLabel}
                onPublish={handlePublish}
            />

            {/* Description edit dialog */}
            <Dialog open={!!descModal} onOpenChange={(open) => !open && setDescModal(null)}>
                <DialogContent className="sm:max-w-[420px] bg-white">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-slate-400" />
                            Edit Description
                        </DialogTitle>
                        {descModal && <p className="text-xs text-gray-400 mt-0.5">{descModal.name}</p>}
                    </DialogHeader>
                    <div className="py-2">
                        <Label htmlFor="flow-desc-edit" className="mb-2 block">Description</Label>
                        <Textarea
                            id="flow-desc-edit"
                            value={editingDesc}
                            onChange={(e) => setEditingDesc(e.target.value)}
                            placeholder="Describe what this flow does..."
                            className="h-28 resize-none"
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDescModal(null)}>Cancel</Button>
                        <Button onClick={saveDescription} disabled={savingDesc} className="bg-primary hover:opacity-90 text-white border-none">
                            {savingDesc ? 'Saving…' : 'Save'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Versions Panel (view-only) */}
            {versionsPanelFlow && (
                <FlowVersionsPanel
                    flowId={versionsPanelFlow.id}
                    flowName={versionsPanelFlow.name}
                    mode="view"
                    onClose={() => setVersionsPanelFlow(null)}
                />
            )}

            {/* Versions Panel (load snapshot) */}
            {loadSnapshotFlow && (
                <FlowVersionsPanel
                    flowId={loadSnapshotFlow.id}
                    flowName={loadSnapshotFlow.name}
                    mode="load"
                    onClose={() => setLoadSnapshotFlow(null)}
                    onLoadSnapshot={handleLoadSnapshot}
                />
            )}
        </StandardPageLayout>
    );
}

// ─────────────────────────────────────────
// Filter Dropdown — icon + checkbox popup
// ─────────────────────────────────────────
function FilterDropdown({ value, onChange }: { value: StatusFilter; onChange: (v: StatusFilter) => void }) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const options: { label: string; val: StatusFilter; color: string }[] = [
        { label: 'All Flows', val: 'all', color: 'text-gray-600 dark:text-slate-300' },
        { label: 'Draft', val: 'Draft', color: 'text-amber-600 dark:text-amber-400' },
        { label: 'Published', val: 'Published', color: 'text-green-600 dark:text-green-400' },
    ];

    const activeLabel = options.find(o => o.val === value)?.label || 'All Flows';
    const hasFilter = value !== 'all';

    return (
        <div className="relative" ref={ref}>
            <button
                onClick={() => setOpen(!open)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-medium transition-all shadow-sm ${
                    hasFilter
                        ? 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-300'
                        : 'bg-white border-gray-200 text-gray-600 dark:bg-slate-900 dark:border-slate-800 dark:text-slate-400'
                } hover:shadow-md`}
            >
                <Filter className="w-3.5 h-3.5" />
                <span>{activeLabel}</span>
                {hasFilter && (
                    <span className="ml-1 flex items-center justify-center w-4 h-4 rounded-full bg-blue-600 text-white text-[9px] font-bold">1</span>
                )}
            </button>
            {open && (
                <div className="absolute left-0 top-full mt-1.5 z-50 bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-gray-100 dark:border-slate-800 py-2 min-w-[160px]">
                    <p className="px-3 py-1 text-[10px] font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider">Filter by Status</p>
                    {options.map(opt => (
                        <button
                            key={opt.val}
                            onClick={() => { onChange(opt.val); setOpen(false); }}
                            className="w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
                        >
                            <span className={`w-4 h-4 rounded flex items-center justify-center border-2 transition-colors ${
                                value === opt.val
                                    ? 'bg-blue-600 border-blue-600'
                                    : 'border-gray-300 dark:border-slate-600'
                            }`}>
                                {value === opt.val && (
                                    <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 10 10">
                                        <path d="M1.5 5l2.5 2.5 4.5-4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                )}
                            </span>
                            <span className={opt.color + ' font-medium'}>{opt.label}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function FlowCard({ flow, onEdit, onPublish, onUnpublish, onVersions, onLoadSnapshot, onDelete, onExport, canExport, onCopy, onDescription, fmtDate }: {
    flow: EtlFlow;
    onEdit: (id: string) => void;
    onPublish: (flow: EtlFlow) => void;
    onUnpublish: (flow: EtlFlow) => void;
    onVersions: (flow: EtlFlow, e: React.MouseEvent) => void;
    onLoadSnapshot: (flow: EtlFlow, e: React.MouseEvent) => void;
    onDelete: (id: string, e: React.MouseEvent) => void;
    onExport: (id: string, name: string, e: React.MouseEvent) => void;
    canExport?: boolean;
    onCopy: (id: string) => void;
    onDescription: (flow: EtlFlow, e: React.MouseEvent) => void;
    fmtDate: (d: string) => string;
}) {
    const { user } = useAuth();
    const isPublished = flow.status === 'Published';
    const isDraft = !flow.status || flow.status === 'Draft';
    const isOwner = flow.is_owner !== false; // default true for backward compat
    const versionLabel = flow.version_number ? `v${flow.version_number}` : null;

    return (
        <div
            onClick={() => onEdit(flow.id)}
            className="group bg-white dark:bg-slate-900 rounded-2xl p-5 border border-gray-100 dark:border-slate-800 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer relative overflow-hidden"
        >
            {/* Top accent bar */}
            <div className={`absolute top-0 left-0 w-full h-1 transition-all duration-300 ${
                isPublished
                    ? 'bg-gradient-to-r from-green-400 to-emerald-400 opacity-20 group-hover:opacity-100'
                    : 'bg-gradient-to-r from-[#002F5D] to-blue-500 opacity-0 group-hover:opacity-100'
            }`} />

            <div className="flex justify-between items-start mb-3">
                <div className="p-2.5 bg-blue-50 dark:bg-slate-800 text-blue-600 dark:text-blue-400 rounded-xl group-hover:bg-[#002F5D] group-hover:text-white transition-colors duration-300">
                    <Activity className="h-5 w-5" />
                </div>
                <div className="flex items-center gap-1.5">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-400 dark:text-slate-500 hover:text-gray-600">
                                <MoreHorizontal className="w-4 h-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48 bg-white dark:bg-slate-900 shadow-xl border-gray-100 dark:border-slate-800">
                            {isOwner ? (
                                <>
                                    {hasPermission(user, PERMISSIONS.FLOWS_UPDATE) && (
                                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit(flow.id); }} className="cursor-pointer dark:text-slate-200 dark:hover:bg-slate-800">
                                            <Edit className="w-4 h-4 mr-2" /> Edit
                                        </DropdownMenuItem>
                                    )}
                                    {hasPermission(user, PERMISSIONS.FLOWS_UPDATE) && (
                                        <DropdownMenuItem onClick={(e) => onDescription(flow, e)} className="cursor-pointer dark:text-slate-200 dark:hover:bg-slate-800">
                                            <FileText className="w-4 h-4 mr-2 text-blue-400" /> Description
                                        </DropdownMenuItem>
                                    )}
                                    {hasPermission(user, PERMISSIONS.FLOWS_PUBLISH) && (
                                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onPublish(flow); }} className="cursor-pointer dark:text-slate-200 dark:hover:bg-slate-800">
                                            <Sparkles className="w-4 h-4 mr-2 text-purple-500" /> Publish As…
                                        </DropdownMenuItem>
                                    )}
                                    {isPublished && hasPermission(user, PERMISSIONS.FLOWS_UNPUBLISH) && (
                                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onUnpublish(flow); }} className="cursor-pointer text-amber-600 dark:text-amber-400 dark:hover:bg-slate-800">
                                            <UploadCloud className="w-4 h-4 mr-2" /> Unpublish
                                        </DropdownMenuItem>
                                    )}
                                </>
                            ) : (
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit(flow.id); }} className="cursor-pointer dark:text-slate-200 dark:hover:bg-slate-800">
                                    <Activity className="w-4 h-4 mr-2 text-blue-400" /> View Flow
                                </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={(e) => onVersions(flow, e)} className="cursor-pointer dark:text-slate-200 dark:hover:bg-slate-800">
                                <History className="w-4 h-4 mr-2 text-blue-500" /> Version History
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onCopy(flow.id); }} className="cursor-pointer dark:text-slate-200 dark:hover:bg-slate-800">
                                <Copy className="w-4 h-4 mr-2" /> Copy ID
                            </DropdownMenuItem>
                            {isOwner && hasPermission(user, PERMISSIONS.FLOWS_DELETE) && (
                                <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={(e) => onDelete(flow.id, e)} className="text-red-600 dark:text-red-400 cursor-pointer focus:bg-red-50">
                                        <Trash2 className="w-4 h-4 mr-2" /> Delete
                                    </DropdownMenuItem>
                                </>
                            )}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>

            <div className="mb-3">
                <h3 className="font-bold text-gray-900 dark:text-slate-100 mb-1 group-hover:text-[#002F5D] dark:group-hover:text-blue-400 transition-colors line-clamp-1">
                    {flow.name}
                </h3>
                <p className="text-xs text-gray-500 dark:text-slate-400 line-clamp-2 min-h-[2.5em]">
                    {flow.description || 'No description provided'}
                </p>
            </div>

            {/* Status + version + shared badge row */}
            <div className="flex items-center gap-2 mb-3 flex-wrap">
                {isPublished ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                        <CheckCircle2 className="w-3 h-3" /> Published
                    </span>
                ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
                        <Edit className="w-3 h-3" /> Draft
                    </span>
                )}
                {versionLabel && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border border-blue-100 dark:border-blue-800">
                        <GitBranch className="w-2.5 h-2.5 mr-1" />
                        {versionLabel}
                    </span>
                )}
                {!isOwner && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400">
                        <FolderOpen className="w-3 h-3" /> Shared
                    </span>
                )}
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-gray-50 dark:border-slate-800 text-xs text-gray-400 dark:text-slate-500">
                <div className="flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5" />
                    <span>{fmtDate(flow.updatedAt)}</span>
                </div>
                {flow.scheduleCron ? (
                    <span className="flex items-center gap-1 text-[#002F5D] bg-blue-50 dark:bg-blue-900/20 dark:text-blue-300 px-2 py-0.5 rounded-full font-medium">
                        <Calendar className="h-3 w-3" /> Scheduled
                    </span>
                ) : (
                    <span>Manual</span>
                )}
            </div>
        </div>
    );
}

// ─────────────────────────────────────────
// Flow List View — Table
// ─────────────────────────────────────────
function FlowListView({ flows, onEdit, onPublish, onUnpublish, onVersions, onLoadSnapshot, onDelete, onExport, canExport, onCopy, onDescription, fmtDate }: {
    flows: EtlFlow[];
    onEdit: (id: string) => void;
    onPublish: (flow: EtlFlow) => void;
    onUnpublish: (flow: EtlFlow) => void;
    onVersions: (flow: EtlFlow) => void;
    onLoadSnapshot: (flow: EtlFlow) => void;
    onDelete: (id: string, e: React.MouseEvent) => void;
    onExport: (id: string, name: string, e: React.MouseEvent) => void;
    canExport?: boolean;
    onCopy: (id: string) => void;
    onDescription: (flow: EtlFlow, e: React.MouseEvent) => void;
    fmtDate: (d: string) => string;
}) {
    const { user } = useAuth();
    return (
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-800 shadow-sm overflow-hidden">
            <Table>
                <TableHeader className="bg-gray-50/50 dark:bg-slate-900/50">
                    <TableRow className="border-b border-gray-200 dark:border-slate-800">
                        <TableHead className="w-[35%] text-gray-500 dark:text-slate-400">Name</TableHead>
                        <TableHead className="text-gray-500 dark:text-slate-400">Status</TableHead>
                        <TableHead className="text-gray-500 dark:text-slate-400">Version</TableHead>
                        <TableHead className="text-gray-500 dark:text-slate-400">Schedule</TableHead>
                        <TableHead className="text-gray-500 dark:text-slate-400">Last Updated</TableHead>
                        <TableHead className="text-right text-gray-500 dark:text-slate-400">Actions</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {flows.map((flow) => {
                        const isPublished = flow.status === 'Published';
                        // Use version_number from API (e.g. 2 → shows 'v2') — same as card view
                        const versionLabel = flow.version_number ? `v${flow.version_number}` : null;

                        return (
                            <TableRow
                                key={flow.id}
                                className="cursor-pointer hover:bg-gray-50/50 dark:hover:bg-slate-800/50 transition-colors border-b border-gray-100 dark:border-slate-800"
                                onClick={() => onEdit(flow.id)}
                            >
                                <TableCell>
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded text-blue-600 dark:text-blue-400">
                                            <Activity className="w-4 h-4" />
                                        </div>
                                        <div>
                                            <div className="font-medium text-gray-900 dark:text-slate-100">{flow.name}</div>
                                            <div className="text-xs text-gray-500 dark:text-slate-400 truncate max-w-[200px]">
                                                {flow.description || "No description"}
                                            </div>
                                        </div>
                                    </div>
                                </TableCell>
                                <TableCell>
                                    <div className="flex items-center gap-1.5">
                                        {isPublished ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                                                <CheckCircle2 className="w-2.5 h-2.5" /> Published
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
                                                <Edit className="w-2.5 h-2.5" /> Draft
                                            </span>
                                        )}
                                    </div>
                                </TableCell>
                                <TableCell>
                                    {versionLabel ? (
                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border border-blue-100 dark:border-blue-800">
                                            <GitBranch className="w-2 h-2 mr-0.5" />{versionLabel}
                                        </span>
                                    ) : (
                                        <span className="text-xs text-gray-400 dark:text-slate-500">—</span>
                                    )}
                                </TableCell>
                                <TableCell>
                                    {flow.scheduleCron ? (
                                        <span className="flex items-center gap-1 text-blue-600 bg-blue-50 dark:bg-blue-900/20 dark:text-blue-300 px-2 py-0.5 rounded-full text-[10px] font-bold w-fit">
                                            <Calendar className="h-3 w-3" /> Scheduled
                                        </span>
                                    ) : (
                                        <span className="text-xs text-gray-500 dark:text-slate-400">Manual</span>
                                    )}
                                </TableCell>
                                <TableCell className="text-gray-500 dark:text-slate-400 text-sm">
                                    {fmtDate(flow.updatedAt)}
                                </TableCell>
                                <TableCell className="text-right">
                                    <div className="flex items-center justify-end gap-1">
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                                <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-400 hover:text-gray-600">
                                                    <MoreHorizontal className="w-4 h-4" />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end" className="w-48 bg-white dark:bg-slate-900 shadow-xl border-gray-100 dark:border-slate-800">
                                                {hasPermission(user, PERMISSIONS.FLOWS_UPDATE) && (
                                                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit(flow.id); }} className="cursor-pointer dark:text-slate-200 dark:hover:bg-slate-800">
                                                        <Edit className="w-4 h-4 mr-2" /> Edit
                                                    </DropdownMenuItem>
                                                )}
                                                {hasPermission(user, PERMISSIONS.FLOWS_PUBLISH) && (
                                                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onPublish(flow); }} className="cursor-pointer dark:text-slate-200 dark:hover:bg-slate-800">
                                                        <Sparkles className="w-4 h-4 mr-2 text-purple-500" /> Publish As…
                                                    </DropdownMenuItem>
                                                )}
                                                {isPublished && hasPermission(user, PERMISSIONS.FLOWS_UNPUBLISH) && (
                                                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onUnpublish(flow); }} className="cursor-pointer text-amber-600 dark:text-amber-400 dark:hover:bg-slate-800">
                                                        <UploadCloud className="w-4 h-4 mr-2" /> Unpublish
                                                    </DropdownMenuItem>
                                                )}
                                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onVersions(flow); }} className="cursor-pointer dark:text-slate-200 dark:hover:bg-slate-800">
                                                    <History className="w-4 h-4 mr-2 text-blue-500" /> Version History
                                                </DropdownMenuItem>
                                                <DropdownMenuSeparator />
                                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onCopy(flow.id); }} className="cursor-pointer dark:text-slate-200 dark:hover:bg-slate-800">
                                                    <Copy className="w-4 h-4 mr-2" /> Copy ID
                                                </DropdownMenuItem>
                                                <DropdownMenuSeparator />
                                                {hasPermission(user, PERMISSIONS.FLOWS_DELETE) && (
                                                    <DropdownMenuItem onClick={(e) => onDelete(flow.id, e)} className="text-red-600 dark:text-red-400 cursor-pointer focus:bg-red-50">
                                                        <Trash2 className="w-4 h-4 mr-2" /> Delete
                                                    </DropdownMenuItem>
                                                )}
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </div>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </div>
    );
}
